import {
  MarketStatus,
  OrderSide,
  OrderType,
} from '@katanaperps/katana-perps-sdk';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { ApiMarket } from '#/api/types';
import BalanceStatsPanelWithData from '#/components/balances/BalanceStatsPanelWithData';
import DepositWithdrawalButtons from '#/components/balances/DepositWithdrawalButtons';
import Button from '#/components/basic/Button/Button';
import ErrorPanel from '#/components/basic/panels/ErrorPanel';
import { closeDisplayedNotification } from '#/components/layout/notifications/notificationsUtils';
import {
  TOOLTIP_MARKET_ORDERS_CLOSED,
  TOOLTIP_POST_ONLY_CLOSED,
} from '#/components/markets/intermittent/intermittentMarketsCopy';
import { useIntermittentMarketSessionDisplay } from '#/components/markets/intermittent/useIntermittentMarketSessionDisplay';
import { getOrderbookSectionHeight } from '#/components/orders/orderBook/orderBookRowUtils';
import TPSLModal from '#/components/positions/takeProfitStopLoss/TPSLModal';
import VaultStatsPanel from '#/components/vaults/components/VaultStatsPanel';
import { useOwnVaultQuery } from '#/components/vaults/shared/utils';
import {
  BASE_MAKER_FEE_OVERRIDE_PERCENT,
  BASE_TAKER_FEE_OVERRIDE_PERCENT,
  HIGH_SPREAD_MARKET_ORDER_PERCENTAGE_LIMIT,
} from '#/config/constants';
import { buildMarketPair } from '#/lib/display/formatting';
import { useSelector, staticDispatch } from '#/reduxState/store';
import { showWalletModal } from '#/reduxState/wallet/walletDetailsReducer';

import BuyFields from './BuyFields';
import { IAction, PanelState } from './buyPanelReducer';
import {
  BuySellPanelInfoBottom,
  BuySellPanelCalculatedInfo,
  MobilePanelInfo,
  TakerMakerFeesTwoColumnData,
} from './buySellInfo';
import BuySellPanelButton from './BuySellPanelButton';
import LeverageFieldsWithModal from './leverage/LeverageFieldsWithModal';
import PanelHeader from './PanelHeader';
import { BuyPanelTopWarning } from './warnings/BuyPanelTopWarning';
import HighSpreadWarningModal from './warnings/HighSpreadWarningModal';
import IsolatedMarginWarningModal from './warnings/IsolatedMarginWarningModal';
import MarketSessionBanner from './warnings/MarketSessionBanner';

const Wrap = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  /* TODO: not sure why but it's needed :o */
  overflow: hidden;

  .isDesktopStyle & {
    justify-content: flex-start;
  }
  .isMobileStyle & {
    background: var(--elevation-light);
    border-radius: 2px;
  }

  /* background: var(--elevation-light);
  margin: 4px;
  border-radius: 2px; */

  /* .isDesktopStyle & {
    overflow-y: auto;
    $ { scrollbarStyle}
  } */
`;

const InnerWrapper = styled.div<{ $minHeight: number }>`
  transition: all 0.5s ease;
  padding: 0 4px;
  width: 100%;
  position: relative;
  .isDesktopStyle & {
    background: var(--elevation-light);
    border-radius: 2px;
    margin-bottom: 4px;
    padding: 8px;
    min-height: ${({ $minHeight }) => $minHeight}px;
  }

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const MobileLeverageAndTypeBlock = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
`;

const FooterWrapper = styled.div`
  flex: 1;
  background: var(--elevation-light);
  border-radius: 2px;
`;

const BalanceActionsWrap = styled.div`
  margin: 0 12px;
  padding: 12px 0;
`;

const LoadingOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: black;

  transition: opacity 1s;
  opacity: ${({ $isVisible }) => ($isVisible ? 0.4 : 0)};
  z-index: ${({ $isVisible }) => ($isVisible ? 123 : -1)};
`;

type Props = {
  dispatch: (action: IAction) => void;
  state: PanelState;
  calculatedInfo: BuySellPanelCalculatedInfo;
  performOrder: (side: OrderSide) => Promise<unknown>;
  token1base: string;
  token2quote: string;
  getBestPrice: () => string;
  getSpreadPercentage: () => number | null;
  priceDecimals: number;
  quantityDecimals: number;
  quantityBelowMinimum: boolean | undefined;
  hasDustPosition: boolean;
  hasOpenPosition: boolean | undefined;
  buyEnabled: boolean;
  sellEnabled: boolean;
  marketData?: ApiMarket;
  buyingPower: number | undefined;
  initialMarginFractionOverride: string | undefined | null;
  walletMakerFee: string | undefined;
  walletTakerFee: string | undefined;
  /** When set, Buy/Sell buttons are disabled with this hover tip (isolated margin rules) */
  tradeRestrictionMessage?: string;
  /** Wallet has an open order or position in the current market (undefined while loading) */
  hasOpenOrderOrPositionInMarket?: boolean;
};

/**
 * Buy sell panel for Base/Quote token pair (eg. IDEX/ETH)
 */
function BuySellPanel({
  dispatch,
  state,
  calculatedInfo,
  performOrder,
  token1base,
  token2quote,
  getBestPrice,
  getSpreadPercentage,
  buyEnabled,
  sellEnabled,
  priceDecimals,
  quantityDecimals,
  quantityBelowMinimum,
  hasDustPosition,
  hasOpenPosition,
  marketData,
  buyingPower,
  initialMarginFractionOverride,
  walletMakerFee,
  walletTakerFee,
  tradeRestrictionMessage,
  hasOpenOrderOrPositionInMarket,
}: Props) {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const ownVault = useOwnVaultQuery();
  const defaultMakerFeeRate = useSelector(
    state => state.exchange.info.defaultMakerFeeRate
  );
  const defaultTakerFeeRate = useSelector(
    state => state.exchange.info.defaultTakerFeeRate
  );
  const isMobile = useSelector(state => state.screen.isMobile);
  const verticalResolutionIndex = useSelector(
    state => state.screen.verticalResolutionIndex
  );
  const closedLargeSpreadMarketOrderWarning = useSelector(
    state => state.exchange.userGuide.closedLargeSpreadMarketOrderWarning
  );
  const isRestrictedRegion = !!useSelector(
    state => state.region.restrictedRegion
  );
  const closedIsolatedMarginWarning = useSelector(
    state => state.exchange.userGuide.closedIsolatedMarginWarning
  );
  const [highSpreadConfirm, setHighSpreadConfirm] = useState<{
    side: OrderSide;
    spread: number;
  } | null>(null);
  const [isolatedMarginConfirm, setIsolatedMarginConfirm] =
    useState<OrderSide | null>(null);

  const session = useIntermittentMarketSessionDisplay(
    marketData,
    'on-state-change'
  );
  const closedForTrading = !!session?.isClosedForTrading;
  // Stop market orders trigger only after an intermittent market reopens, so
  // they stay available while it is closed — unlike on post-only markets
  const stopMarketDisabled = marketData?.status === MarketStatus.limitMakerOnly;
  const limitOnlyReason = closedForTrading
    ? TOOLTIP_MARKET_ORDERS_CLOSED
    : state.limitMakerOnlyMarket
    ? 'This market is in Post-Only mode.'
    : undefined;
  const postOnlyForcedReason = state.limitMakerOnlyMarket
    ? closedForTrading
      ? TOOLTIP_POST_ONLY_CLOSED
      : 'This market is in Post-Only mode.'
    : undefined;
  const formRef = useRef<HTMLFormElement>(null);
  const handleSubmitCheck = (event: React.FormEvent<HTMLFormElement>) => {
    // Run HTML5 form input checks (html5 validation)
    event.preventDefault();
  };

  const handlePlaceOrder = (
    side: OrderSide,
    highSpreadConfirmed?: true,
    isolatedMarginConfirmed?: true
  ) => {
    if (!formRef.current) {
      console.error('buysell - No formRef');
      return;
    }
    const isValid = formRef.current.reportValidity();
    if (!isValid) {
      // HTML should highlight problems for
      console.log('Form not valid');
      return;
    }
    // Warn on the first order in an isolated margin market (skipped once the
    // wallet already has an open order or position in it)
    if (
      !isolatedMarginConfirmed &&
      marketData?.marginType === 'isolated' &&
      !hasOpenOrderOrPositionInMarket &&
      !closedIsolatedMarginWarning
    ) {
      setIsolatedMarginConfirm(side);
      return;
    }
    // For market orders with high spread, we want to inform user first
    if (closedLargeSpreadMarketOrderWarning) {
      // Warning already closed with "Do not show again"
    } else if (highSpreadConfirmed === true) {
      setHighSpreadConfirm(null);
    } else if (state.orderType === OrderType.market) {
      const spread = getSpreadPercentage();
      if (
        spread !== null &&
        spread > HIGH_SPREAD_MARKET_ORDER_PERCENTAGE_LIMIT
      ) {
        setHighSpreadConfirm({ side, spread });
        return;
      }
    }
    performOrder(side).catch(error => {
      console.error(error);
      dispatch({ type: 'error', message: error.message });
    });
  };

  const isLoading =
    state.state === 'loadingBuy' || state.state === 'loadingSell';

  const marketSymbol = buildMarketPair(token1base, token2quote);

  const determineFee = (
    defaultRate?: string,
    marketRate?: string,
    walletRate?: string
  ) =>
    !defaultRate || !marketRate || (walletAddress && !walletRate)
      ? undefined // Initializing
      : walletAddress
      ? Math.min(Number(defaultRate), Number(marketRate), Number(walletRate)) *
        100
      : Math.min(Number(defaultRate), Number(marketRate)) * 100;

  const takerFee = determineFee(
    defaultTakerFeeRate,
    marketData?.takerFeeRate,
    walletTakerFee
  );
  const makerFee = determineFee(
    defaultMakerFeeRate,
    marketData?.makerFeeRate,
    walletMakerFee
  );

  // Base (pre-discount) fees struck-through next to the discounted fee.
  // Use the hardcoded override when defined, otherwise the market default.
  const determineMarketMaxFee = (defaultRate?: string, marketRate?: string) =>
    !defaultRate || !marketRate
      ? undefined
      : Math.min(Number(defaultRate), Number(marketRate)) * 100;

  const baseTakerFee =
    BASE_TAKER_FEE_OVERRIDE_PERCENT ??
    determineMarketMaxFee(defaultTakerFeeRate, marketData?.takerFeeRate);
  const baseMakerFee =
    BASE_MAKER_FEE_OVERRIDE_PERCENT ??
    determineMarketMaxFee(defaultMakerFeeRate, marketData?.makerFeeRate);

  return (
    <Wrap>
      {state.conditionalTpSlModal && marketSymbol && (
        <TPSLModal
          market={marketSymbol}
          initialStopLossPrice={state.conditionalTpSl?.stopLossPrice}
          initialTakeProfitPrice={state.conditionalTpSl?.takeProfitPrice}
          futureTpSl={{
            initialStopLossType: state.conditionalTpSl?.stopLossPriceType,
            initialTakeProfitType: state.conditionalTpSl?.takeProfitPriceType,
            parentOrderId: undefined,
            parentOrderSide: undefined,
            parentBuyQuantity: calculatedInfo.quantityBuy,
            parentSellQuantity: calculatedInfo.quantitySell,
            parentPriceType: state.triggerEnabled
              ? 'stop'
              : state.orderType === 'market'
              ? 'market'
              : 'limit',
            parentPrice:
              state.orderType === 'limit'
                ? state.price
                : state.triggerEnabled
                ? state.triggerPrice
                : undefined,
            onSaveOverride: async ({ side, takeProfit, stopLoss }) => {
              dispatch({
                type: 'setConditionalTpSl',
                tpsl: {
                  side,
                  stopLossPrice: stopLoss?.triggerPrice,
                  stopLossPriceType: stopLoss?.triggerPrice
                    ? stopLoss?.triggerType
                    : undefined,
                  takeProfitPrice: takeProfit?.triggerPrice,
                  takeProfitPriceType: takeProfit?.triggerPrice
                    ? takeProfit.triggerType
                    : undefined,
                },
              });
            },
          }}
          onClose={() => {
            dispatch({ type: 'showConditionlTpSlModal', open: false });
          }}
        />
      )}

      <InnerWrapper
        $minHeight={getOrderbookSectionHeight(verticalResolutionIndex)}
      >
        <div>
          {isMobile ? (
            <MobileLeverageAndTypeBlock>
              <LeverageFieldsWithModal
                marketInitialMarginFractionOverride={
                  initialMarginFractionOverride
                }
                dispatch={dispatch}
                state={state}
                marketData={marketData}
              />
              <div style={{ flex: 1 }}>
                <PanelHeader
                  selectedType={state.orderType}
                  limitOnly={state.limitMakerOnlyMarket}
                  limitOnlyReason={limitOnlyReason}
                  stopMarketDisabled={stopMarketDisabled}
                  acceptingInput={state.state === 'input'}
                  triggerEnabled={state.triggerEnabled}
                  onChangeType={type =>
                    dispatch({ type: 'changeType', orderType: type })
                  }
                />
              </div>
            </MobileLeverageAndTypeBlock>
          ) : (
            <LeverageFieldsWithModal
              marketInitialMarginFractionOverride={
                initialMarginFractionOverride
              }
              dispatch={dispatch}
              state={state}
              marketData={marketData}
            />
          )}

          {marketData?.status === MarketStatus.limitMakerOnly && (
            <BuyPanelTopWarning isPostOnly />
          )}

          {highSpreadConfirm && (
            <HighSpreadWarningModal
              spreadPercentage={highSpreadConfirm.spread}
              onClose={() => setHighSpreadConfirm(null)}
              onContinue={() =>
                handlePlaceOrder(highSpreadConfirm.side, true, true)
              }
            />
          )}

          {isolatedMarginConfirm && marketSymbol && (
            <IsolatedMarginWarningModal
              market={marketSymbol}
              onClose={() => setIsolatedMarginConfirm(null)}
              onContinue={() => {
                const side = isolatedMarginConfirm;
                setIsolatedMarginConfirm(null);
                handlePlaceOrder(side, undefined, true);
              }}
            />
          )}

          <LoadingOverlay $isVisible={isLoading} />

          {!isMobile && (
            <PanelHeader
              selectedType={state.orderType}
              limitOnly={state.limitMakerOnlyMarket}
              limitOnlyReason={limitOnlyReason}
              stopMarketDisabled={stopMarketDisabled}
              acceptingInput={state.state === 'input'}
              triggerEnabled={state.triggerEnabled}
              onChangeType={type =>
                dispatch({ type: 'changeType', orderType: type })
              }
            />
          )}
          {/* Only for validation ??? TODO: works ? */}
          <form onSubmit={handleSubmitCheck} ref={formRef}>
            <BuyFields
              isDisabled={isRestrictedRegion}
              token2quote={token2quote}
              token1base={token1base}
              panelState={state}
              panelDispatch={dispatch}
              getBestPrice={getBestPrice}
              priceDecimals={priceDecimals}
              quantityDecimals={quantityDecimals}
              quantityBelowMinimum={quantityBelowMinimum}
              hasDustPosition={hasDustPosition}
              hasOpenPosition={hasOpenPosition}
              marketData={marketData}
              calculatedInfo={calculatedInfo}
              buyingPower={buyingPower}
              postOnlyForcedReason={postOnlyForcedReason}
            />
          </form>

          {state.errorMessage && (
            <ErrorPanel marginTop={16} message={state.errorMessage} />
          )}
          {state.requiresReauthorize && (
            <div style={{ marginTop: 8 }}>
              <Button
                onClick={() => {
                  closeDisplayedNotification('delegatedKeyInvalid');
                  closeDisplayedNotification('delegatedKeyExpired');

                  staticDispatch(
                    showWalletModal({ keepWalletUntilSuccess: true })
                  );
                }}
              >
                Re-Authorize
              </Button>
            </div>
          )}

          {/* TODO: remove ? */}
          <div style={{ padding: isMobile ? 4 : 8 }} />
        </div>
        <div>
          <MarketSessionBanner sessionInfo={marketData} />
          {isMobile ? (
            <>
              {(!state.conditionalTpSl ||
                state.conditionalTpSl.side === 'long') && (
                <>
                  <BuySellPanelButton
                    onPlaceOrder={handlePlaceOrder}
                    buyEnabled={buyEnabled}
                    sellEnabled={sellEnabled}
                    panelState={state.state}
                    singleAction="buy"
                    disabledReason={tradeRestrictionMessage}
                  />
                  <div style={{ padding: '4px 0 8px' }}>
                    <MobilePanelInfo
                      calculatedInfo={calculatedInfo}
                      side="buy"
                      token1base={token1base}
                      token2quote={token2quote}
                    />
                  </div>
                </>
              )}
              {(!state.conditionalTpSl ||
                state.conditionalTpSl?.side === 'short') && (
                <>
                  <BuySellPanelButton
                    onPlaceOrder={handlePlaceOrder}
                    buyEnabled={buyEnabled}
                    sellEnabled={sellEnabled}
                    panelState={state.state}
                    singleAction="sell"
                    disabledReason={tradeRestrictionMessage}
                  />
                  <div style={{ padding: '4px 0 4px' }}>
                    <MobilePanelInfo
                      calculatedInfo={calculatedInfo}
                      side="sell"
                      token1base={token1base}
                      token2quote={token2quote}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <BuySellPanelButton
              // This will not be "submit" button,
              // to prevent submitting order by hitting Enter in input fields
              onPlaceOrder={handlePlaceOrder}
              buyEnabled={buyEnabled}
              sellEnabled={sellEnabled}
              panelState={state.state}
              disabledReason={tradeRestrictionMessage}
              singleAction={
                state.conditionalTpSl?.side
                  ? state.conditionalTpSl.side === 'long'
                    ? 'buy'
                    : 'sell'
                  : undefined
              }
            />
          )}
          {isMobile ? (
            <TakerMakerFeesTwoColumnData
              makerFee={makerFee}
              takerFee={takerFee}
              baseMakerFee={baseMakerFee}
              baseTakerFee={baseTakerFee}
            />
          ) : (
            <>
              <div style={{ padding: 4 }} />
              <BuySellPanelInfoBottom
                calculatedInfo={calculatedInfo}
                makerFee={makerFee}
                takerFee={takerFee}
                baseMakerFee={baseMakerFee}
                baseTakerFee={baseTakerFee}
              />
            </>
          )}
        </div>
      </InnerWrapper>

      {!isMobile && (
        <FooterWrapper>
          <div
            style={{
              padding: '8px 8px 0 8px',
            }}
          >
            <BalanceStatsPanelWithData />
            {ownVault && <VaultStatsPanel managerWallet={ownVault.manager} />}
          </div>
          <BalanceActionsWrap>
            <DepositWithdrawalButtons underEachOther />
          </BalanceActionsWrap>

          {/* <div style={{ height: 32 }} />
          <BalanceActionsWrap>
            <DepositWithdrawalButtons />
          </BalanceActionsWrap>
          <div style={{ padding: '0 12px 12px' }}>
            <BalanceStatsPanel />
          </div> */}
        </FooterWrapper>
      )}
    </Wrap>
  );
}

export default BuySellPanel;
